import { combineReducers, createReducer } from '@reduxjs/toolkit';

import { setResponse, setResponseExtra } from 'Core/actions/response';
import type { PreselectionTreeLevel } from 'Models/facetRequest';
import { FacetCollection, type FacetValueFull, SearchItemDataCollection } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import uiConfig from 'Models/uiConfig/uiConfig';
import { parseFlagString } from 'Modules/serverApi/common/flags';
import { SEARCH_STATE_FLAGS, type SearchStateFlag, type ServerModel } from 'Modules/serverApi/types';

const { customFacetValueFilter: customFilter } = uiConfig;

const normalStateMap = {
  Corrected: 'corrected',
  Phonetic: 'corrected',
  Nothing: 'nothing',
  Normal: 'normal',
  Partial: 'partial',
} satisfies Partial<Record<SearchStateFlag, string>>;

type SearchResponseState = {
  error: ServerModel.SearchResponse['_clientError'];
  facets: FacetCollection;
  items: SearchItemDataCollection;
  messages: ServerModel.SearchResponse['Messages'];
  originalQuery: ServerModel.SearchResponse['OriginalQuery'];
  query: ServerModel.SearchResponse['Query'];
  state: string;
  totalHits: ServerModel.SearchResponse['TotalHits'];
  extra: ServerModel.SearchResponse['Extra'] | null;
};

const initStates: SearchResponseState = {
  error: null,
  facets: new FacetCollection(),
  items: new SearchItemDataCollection(),
  messages: [],
  originalQuery: '',
  query: '',
  state: '',
  totalHits: -1,
  extra: null,
};

export function normalizeSearchState(state: string): string {
  const [baseState] = parseFlagString(state, SEARCH_STATE_FLAGS);
  return baseState && baseState in normalStateMap
    ? normalStateMap[baseState as keyof typeof normalStateMap]
    : state;
}

export default combineReducers({
  error: createReducer(initStates.error, (builder) =>
    builder.addCase(setResponse, (_state, { payload }) => payload._clientError ?? null),
  ),
  facets: createReducer(initStates.facets, (builder) =>
    builder.addCase(setResponse, (_state, { payload: { Facets }, meta: { preselection } }) =>
      prepareResponseFacets(Facets, preselection, (f) => !fitmentSearchConfig.fields.includes(f.FieldName)),
    ),
  ),
  items: createReducer(initStates.items, (builder) =>
    builder.addCase(setResponse, (state, { payload: { Items }, meta: { appendItems } }) => {
      const newItems = new SearchItemDataCollection(Items, 'search');
      return appendItems ? state.concat(newItems) : newItems;
    }),
  ),
  messages: createReducer(initStates.messages, (builder) =>
    builder.addCase(setResponse, (_state, { payload }) => payload.Messages),
  ),
  originalQuery: createReducer(initStates.originalQuery, (builder) =>
    builder.addCase(setResponse, (_state, { payload }) => payload.OriginalQuery),
  ),
  query: createReducer(initStates.query, (builder) =>
    builder.addCase(setResponse, (_state, { payload }) => payload.Query),
  ),
  state: createReducer(initStates.state, (builder) =>
    builder.addCase(setResponse, (_state, { payload: { State } }) =>
      State ? normalizeSearchState(State) : _state,
    ),
  ),
  totalHits: createReducer(initStates.totalHits, (builder) =>
    builder.addCase(setResponse, (_state, { payload }) => payload.TotalHits),
  ),
  extra: createReducer(initStates.extra, (builder) =>
    builder
      .addCase(setResponse, (_state, { payload }) => payload.Extra)
      .addCase(setResponseExtra, (_state, { payload }) => payload),
  ),
});

type RawFacet = ServerModel.FacetCategory;

export function prepareResponseFacets(
  facets: RawFacet[] | null | undefined,
  preselection: PreselectionTreeLevel[],
  filter?: (f: RawFacet) => boolean,
): FacetCollection {
  /*
    defaultFilter: Don't display preselected facets without the treeLevel.
    Example: the brand PLP will have the 'Brands' preselected facet. There will be only one selected value
    in the response, so don't display this facet with only one selected value which can't be unselected.
  */
  const preselectedFields = preselection.map((v) => v.field);
  const defaultFilter = (v: FacetValueFull) => !(v.treeLevel === null && preselectedFields.includes(v.field));
  const facetValueFilter = customFilter
    ? (v: FacetValueFull) => customFilter(v, { filter: defaultFilter, preselection })
    : defaultFilter;

  // preselected using 'getLocalPreselection' functions in platforms and config.js files
  const preselectedTreeLevelMap = Object.fromEntries(
    preselection
      .filter((v): v is PreselectionTreeLevel & { treeLevel: number } => typeof v.treeLevel === 'number')
      .map((v) => [v.field, v.treeLevel + 1]),
  );

  const responseFacets = (filter ? facets?.filter((f) => filter(f)) : facets) ?? [];

  return new FacetCollection(responseFacets, facetValueFilter, preselectedTreeLevelMap);
}
